import {
  DeleteTwoTone,
  PlusCircleTwoTone,
  SaveOutlined,
} from '@ant-design/icons';
import { Alert, Input, Modal } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Sort } from 'src/icons/Sort.icon';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getOrganizationByIdSlice } from 'src/modules/SuperAdmin/store/slices/organization.slice';
import { listPractitionersFromOrg } from 'src/store/actions/practitioner.action';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import { SuperAdminTable } from '../../../baseComponents/Table';
import {
  getSRProviderMappings,
  updateSRMappings,
} from 'src/modules/SuperAdmin/store/actions/service_request';
import { getOrgSetting } from 'src/store/actions/settings.action';

const ProviderMappingPage = () => {
  const dispatch = useDispatch();

  const { allPractitionerNames, allPractitionerNamesLoading } = useSelector(
    (state) => state?.practitionerState
  );
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { srProviderMappings, srProviderMappingLoading } = useSelector(
    (state) => state?.serviceRequestState
  );
  const [practitionerMapping, setPractitionerMapping] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [addPractitionerBoxVisible, setAddPractitionerBoxVisible] =
    useState(false);
  const [addedPractitioner, setAddedPractitioner] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableLoader, setTableLoader] = useState(false);
  const [bulkSRUploadEnabled, setBulkSRUploadEnabled] = useState(
    currentPractitioner?.org_settings?.enable_service_request_upload ?? false
  );
  const [organizationDropdown, setOrganizationDropdown] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    const savedOrg = getFromStorage('superAdminSelectedOrganization');
    const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
    return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
  });

  const [organizationName, setOrganizationName] = useState(null);
  const [organization, setOrganization] = useState(currentPractitioner?.org_id);
  const [updatedValues, setUpdatedValues] = useState({});

  useEffect(() => {
    setTableLoader(allPractitionerNamesLoading && srProviderMappingLoading);
  }, [allPractitionerNamesLoading, srProviderMappingLoading]);

  useEffect(() => {
    dispatch(listPractitionersFromOrg(selectedOrganization));
    dispatch(getAllActiveOrganizationAction());
    dispatch(getOrganizationByIdSlice(null));
  }, [dispatch]);

  useEffect(() => {
    if (selectedOrganization) {
      dispatch(getSRProviderMappings(selectedOrganization));
      dispatch(listPractitionersFromOrg(selectedOrganization));
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key, index) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      setOrganizationDropdown(dataArr);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (
      allPractitionerNames &&
      Array.isArray(allPractitionerNames) &&
      allPractitionerNames.length > 0
    ) {
      const initialValues = {};
      allPractitionerNames.forEach((list) => {
        if (list?.org_uuid === selectedOrganization) {
          const fullName = list?.practitioner_name;
          initialValues[fullName] =
            practitionerMapping && practitionerMapping[fullName]
              ? practitionerMapping[fullName]
              : [fullName];
        }
      });
      setUpdatedValues(initialValues);
    }
  }, [allPractitionerNames, practitionerMapping, selectedOrganization]);

  useEffect(() => {
    if (
      allPractitionerNames &&
      Array.isArray(allPractitionerNames) &&
      allPractitionerNames.length > 0
    ) {
      let dataArray = allPractitionerNames?.map((list, index) => {
        return {
          key: index,
          org_uuid: list?.org_uuid,
          full_name: list?.practitioner_name,
        };
      });
      if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
        if (selectedOrganization === null) {
          setTableData(dataArray);
        } else {
          dataArray = dataArray.filter(
            (item) => item.org_uuid === selectedOrganization
          );
          setTableData(dataArray);
        }
      }
    } else {
      setTableData([]);
    }
  }, [allPractitionerNames, selectedOrganization]);

  useEffect(() => {
    setPractitionerMapping(srProviderMappings);
  }, [srProviderMappings, selectedOrganization]);

  const practitionerNameSorter = (a, b) => {
    return a.full_name.localeCompare(b.full_name);
  };

  useEffect(() => {
    async function getOrgSettingData() {
      await dispatch(getOrgSetting(selectedOrganization)).then((setting) => {
        setBulkSRUploadEnabled(setting?.enable_service_request_upload ?? false);
      });
    }
    getOrgSettingData();
  }, [selectedOrganization, dispatch]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganization(newOrg);
        handleSelectChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSelectChange = (option) => {
    const orgId = option?.org_uuid;
    setSelectedOrganization(orgId);
    setOrganizationName(option);
    setOrganization(option?.value);
  };

  const handleInputChange = (e, fullName) => {
    setUpdatedValues({
      ...updatedValues,
      [fullName]: e.target.value,
    });
  };

  const handleAddPractitionerSubmit = () => {
    updatedValues[selectedPractitioner] = [
      ...updatedValues[selectedPractitioner],
      addedPractitioner.trim(),
    ];

    setAddPractitionerBoxVisible(false);
    setSelectedPractitioner(null);
    setAddedPractitioner(null);
  };

  const handleAddPractitionerClose = () => {
    setAddPractitionerBoxVisible(false);
    setSelectedPractitioner(null);
    setAddedPractitioner(null);
  };

  const handleFormSubmit = (values) => {
    let newReverseMapping = {};
    for (const [key, value] of Object.entries(updatedValues)) {
      for (const practitioner of value) {
        if (practitioner !== key) {
          newReverseMapping[practitioner] = key;
        }
      }
    }
    let payload = {
      settings: {
        practitioner_mapping: newReverseMapping,
      },
    };
    dispatch(
      updateSRMappings(payload, selectedOrganization, 'provider mapping')
    );
  };

  const handleDeletePractitioner = (
    recognizedPractitionerName,
    practitionerToDelete
  ) => {
    setUpdatedValues((prevValues) => ({
      ...prevValues,
      [recognizedPractitionerName]: prevValues[
        recognizedPractitionerName
      ].filter((practitioner) => practitioner !== practitionerToDelete),
    }));
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Practitioner name in Insight{' '}
          {tableData?.length > 0 && (
            <Sort className={'sort-icon-provider-mapping'} />
          )}
        </span>
      ),
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: tableData?.length > 0 && practitionerNameSorter,
      width: '50%',
    },
    {
      title: <span className="font-[700]"> Practitioner name in CSV</span>,
      width: '60%',
      render: (text, record) => {
        const practitionerNames = updatedValues[record.full_name]?.map((name) =>
          name?.trim()
        );
        return (
          <div className="flex flex-wrap gap-2">
            {practitionerNames?.map((name, index) => {
              return (
                <div
                  key={index}
                  className="relative group inline-flex items-center px-3 py-1 mt-2 rounded-3xl"
                  style={{ border: `1px solid #00D090` }}
                >
                  <span className="flex-grow text-center group-hover:pr-4 transition-all whitespace-pre-line">
                    {name}
                  </span>
                  <DeleteTwoTone
                    className="absolute right-1 hidden group-hover:inline-block cursor-pointer rounded-full p-1 transition-all"
                    onClick={() =>
                      handleDeletePractitioner(record.full_name, name)
                    }
                    twoToneColor="#eb2f96"
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      width: '25%',
      render: (text, record) => {
        return (
          <PlusCircleTwoTone
            twoToneColor="#00D090"
            style={{ fontSize: '19px' }}
            onClick={() => {
              setSelectedPractitioner(record.full_name);
              setAddPractitionerBoxVisible(true);
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      <div>
        <Formik initialValues={{}} onSubmit={handleFormSubmit}>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-row sm:justify-between gap-4 lg:justify-between items-center">
                <div className="flex flex-col justify-center"></div>
                {bulkSRUploadEnabled && (
                  <div className="flex justify-end items-end">
                    <button
                      type="submit"
                      className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                    >
                      <SaveOutlined
                        className={`mr-2`}
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                      />{' '}
                      Save all
                    </button>
                  </div>
                )}
              </div>

              {bulkSRUploadEnabled ? (
                <div className="super-admin-table">
                  <div className="mt-5 ant-table rounded-xl shadow-xl">
                    <SuperAdminTable
                      className={`appointment-page-table`}
                      columns={columns}
                      data={tableData}
                      loading={tableLoader}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center p-4 min-h-[60vh]">
                  <Alert
                    message="Bulk Service request CSV uploads are currently disabled for this organization."
                    type="info"
                    showIcon
                  />
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
      <Modal
        open={addPractitionerBoxVisible}
        closable={false}
        centered={true}
        styles={{ body: { minHeight: '80px' } }}
        footer={[
          <div className="flex justify-end gap-1">
            <button
              key="back"
              className="bg-[#A9A9A9] hover:bg-[#899499] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleAddPractitionerClose}
            >
              Cancel
            </button>
            <button
              key="submit"
              className="bg-[#00D090] hover:bg-[#059669] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleAddPractitionerSubmit}
              disabled={!addedPractitioner || addedPractitioner.trim() === ''}
            >
              Add practitioner
            </button>
          </div>,
        ]}
      >
        <div className="mb-4 flex flex-col gap-2">
          <p className="text-md">{selectedPractitioner}</p>
        </div>
        <div>
          <Input.TextArea
            placeholder="Enter practitioner name"
            onChange={(e) => setAddedPractitioner(e.target.value)}
            value={addedPractitioner}
            autoSize={true}
          />
        </div>
      </Modal>
    </>
  );
};

export default ProviderMappingPage;
